import React, { useEffect, useMemo } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DoubleLabelSelect } from '@node-space/storybook-components/dist/DoubleLabelSelect'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Box as Container,
  Dialog,
  Input,
  Box as LoadingContainer,
} from '@node-space/ui-components'
import Loader from 'components/spinners/LoadingAnimationDark'
import { PathNames } from 'constants/General'
import {
  bvnkNetworkConnectionsAtom,
  isFetchingConnectionsAtom,
  isFetchingConnectionsErrorAtom,
} from 'pages/BvnkNetwork/atoms/bvnkNetworkAtoms'
import { formatString, generateReference, roundNumberWithCommas } from 'utils/utils'
import { selectedBvnkNetworkBeneficiaryAtom, selectedWalletAtom } from './atoms/atoms'
import { PayActionProps } from './Pay'

const PayFormBvnkNetworkBeneficiary = ({
  action,
  wallets,
  form,
  loading: isLoading,
  isFetchingWallets,
  registerFormField,
  dismissAction,
  setStep,
}: PayActionProps) => {
  const { t } = useTranslation()
  const formValues = form.watch()

  const bvnkNetworkConnections = useAtomValue(bvnkNetworkConnectionsAtom)
  const isFetchingConnections = useAtomValue(isFetchingConnectionsAtom)
  const isFetchingConnectionsError = useAtomValue(isFetchingConnectionsErrorAtom)
  const sourceWallet = useAtomValue(selectedWalletAtom)
  const setSelectedBvnkNetworkBeneficiaryAtom = useSetAtom(selectedBvnkNetworkBeneficiaryAtom)

  const selectedNetworkBeneficiary = useMemo(() => {
    if (!formValues?.beneficiarySelection && !bvnkNetworkConnections?.connected?.length) return

    return bvnkNetworkConnections?.connected?.find(
      connection => connection?.id === formValues?.beneficiarySelection
    )
  }, [formValues?.beneficiarySelection, bvnkNetworkConnections?.connected])

  useEffect(() => {
    form?.setValue('reference', generateReference())
  }, [formValues?.walletId])

  useEffect(() => {
    setSelectedBvnkNetworkBeneficiaryAtom(selectedNetworkBeneficiary)
  }, [selectedNetworkBeneficiary])

  useEffect(() => {
    // When making network transfer by choosing the beneficiary first
    // Get the linked wallet, and set it
    if (
      !!selectedNetworkBeneficiary &&
      !!action?.metadata?.pay?.beneficiaryOptionId &&
      !formValues?.walletId
    ) {
      const wallet = wallets?.find(
        wallet => wallet?.lsid === selectedNetworkBeneficiary?.wallets?.[0]?.walletId
      )
      form.setValue('walletId', wallet?.id?.toString())
    }
  }, [
    selectedNetworkBeneficiary,
    action?.metadata?.pay?.beneficiaryOptionId,
    wallets,
    formValues?.walletId,
  ])

  const isInsufficientBalance = sourceWallet?.balance <= 0

  const beneficiaryOptions = useMemo(() => {
    return bvnkNetworkConnections?.connected?.map(connection => {
      const { details, id } = connection || {}
      return {
        icon: details?.name ?? '',
        label: details?.name,
        value: id,
        secondLabel: null,
        secondLabelText: details?.description || ' ',
        customIcon: (
          <Box className="pt-0.5">
            <Avatar type="entity" name={details?.name ?? ''} />
          </Box>
        ),
        hideSecondLabelDelimiter: true,
      }
    })
  }, [bvnkNetworkConnections?.connected])

  const selectBeneficiaryDropdownPlaceholder = useMemo(() => {
    if (!beneficiaryOptions?.length) {
      return t('manageBeneficiaries.noBeneficiaries')
    } else if (!!beneficiaryOptions?.length && !selectedNetworkBeneficiary) {
      return t('manageBeneficiaries.beneficiarySelect')
    }
  }, [beneficiaryOptions?.length, selectedNetworkBeneficiary])

  const errorCalloutMessage = useMemo(() => {
    if (isFetchingConnectionsError) {
      return t('bvnkNetwork.connectionsListError')
    }
  }, [isFetchingConnectionsError])

  const isShowingInsufficientError = isInsufficientBalance && !errorCalloutMessage
  const isShowingErrorCalloutMessage = !!errorCalloutMessage
  const isShowingBeneficiarySelect =
    !isInsufficientBalance &&
    !errorCalloutMessage &&
    beneficiaryOptions?.length &&
    !isLoading &&
    !isFetchingConnections &&
    (sourceWallet || action?.metadata?.pay?.beneficiaryOptionId)

  const onSubmit: SubmitHandler<typeof formValues> = async () => {
    setStep('amount')
  }

  const onWalletChange = walletId => {
    form.setValue('walletId', walletId)
  }

  const onBeneficiaryChange = beneficiaryReference => {
    form.setValue('beneficiarySelection', beneficiaryReference)
  }

  const isLoadingForm = isLoading || isFetchingConnections || isFetchingWallets

  return (
    <Dialog
      open
      title={t('whoToPay')}
      onClose={dismissAction}
      type="utility"
      ActionButtons={[
        <Button key="cancel" variant="secondary" onClick={dismissAction}>
          {t('cancel')}
        </Button>,
        <Button
          key="payRecipient"
          testId={t('payRecipient')}
          onClick={form.handleSubmit(onSubmit)}
          loading={isLoading || isFetchingConnections}
          disabled={
            !selectedNetworkBeneficiary ||
            !beneficiaryOptions?.length ||
            isInsufficientBalance ||
            !sourceWallet
          }
        >
          {t('payRecipient')}
        </Button>,
      ]}
    >
      <form>
        <Container flexCol gapY={24}>
          <DoubleLabelSelect
            disabled
            iconsUrl={process?.env?.ICONS_URL}
            label={t('wallet')}
            value={formValues?.walletId}
            placeholder={!sourceWallet ? t('noWallets') : t('searchOrSelectAWallet')}
            name="currencyCode"
            skeletonLoading={isFetchingWallets}
            options={
              sourceWallet && [
                {
                  label: sourceWallet?.walletName,
                  value: sourceWallet?.legacyWalletId
                    ? sourceWallet?.legacyWalletId?.toString()
                    : sourceWallet?.walletId,
                  icon: sourceWallet?.currency?.toLowerCase(),
                  secondLabel: formatString(
                    t('balanceWithCurrency'),
                    roundNumberWithCommas(sourceWallet?.balance, 2),
                    sourceWallet?.currency
                  ),
                },
              ]
            }
            onChange={onWalletChange}
          />
          {isLoadingForm && (
            <LoadingContainer flex justify="center" paddingT={32}>
              {Loader}
            </LoadingContainer>
          )}
          {!isLoadingForm && (
            <>
              {isShowingInsufficientError && (
                <Callout variant="error">
                  {t('payInsufficient', { currencyCode: sourceWallet?.currency })}
                </Callout>
              )}

              {isShowingErrorCalloutMessage && (
                <Callout variant="error">{errorCalloutMessage}</Callout>
              )}

              {isShowingBeneficiarySelect ? (
                <>
                  <DoubleLabelSelect
                    disabled={!beneficiaryOptions?.length}
                    iconsUrl={process.env.ICONS_URL}
                    label={t('manageBeneficiaries.selectBeneficiary')}
                    menuMaxHeight={300}
                    name="beneficiarySelection"
                    options={beneficiaryOptions}
                    placeholder={selectBeneficiaryDropdownPlaceholder}
                    value={formValues?.beneficiarySelection}
                    onChange={onBeneficiaryChange}
                    helperText={t('payouts.beneficiaryFiltersDisclaimer')}
                  />
                </>
              ) : (
                !isShowingErrorCalloutMessage &&
                !isShowingInsufficientError && (
                  <Callout
                    variant="warning"
                    GhostButton={
                      <Button
                        onClick={() =>
                          window.open(
                            `${PathNames.BANK_BENEFICIARIES}?callbackUrl=${location.pathname}`,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                      >
                        {t('addNewBeneficiaryWithoutPlus')}
                      </Button>
                    }
                  >
                    {t('payouts.noSupportedBeneficiaries')}
                  </Callout>
                )
              )}
              {!isShowingErrorCalloutMessage && (
                <Input
                  {...registerFormField('reference')}
                  value={formValues?.reference}
                  placeholder={t('inputPlaceholders.beneficiaryPaymentReferencePlaceHolder')}
                  onChange={({ target: { value } }) => {
                    form.setValue('reference', value, { shouldValidate: true })
                  }}
                />
              )}
            </>
          )}
        </Container>
      </form>
    </Dialog>
  )
}

export default React.memo(PayFormBvnkNetworkBeneficiary)
